<template>
  <div class="auth-page">
    <div class="auth-topbar">
      <div class="auth-brand">
        <b style="font-weight: 800; color: #e8e8e6; font-size: 0.85em; letter-spacing: -0.02em;">turbo</b><span style="font-weight: 300; color: #9ad12b; font-size: 0.85em;">hammer</span>
      </div>
      <modal-help topic="account-login" label="Help for this screen" show-label />
    </div>
    <div class="auth-container">
      <div class="auth-card">
        <template v-if="step === 'password'">
          <h2 class="auth-title">sign in</h2>
          <p class="auth-subtitle">Use the email and password for this account.</p>
          <form @submit.prevent="login" class="auth-form">
            <div class="auth-field">
              <label class="auth-label" for="login-email">email</label>
              <input id="login-email" v-model="email" type="email" placeholder="your email address" required autocomplete="email" class="auth-input" />
            </div>
            <div class="auth-field">
              <label class="auth-label" for="login-password">password</label>
              <input id="login-password" v-model="password" type="password" placeholder="your password" required autocomplete="current-password" class="auth-input" />
            </div>
            <div v-if="errorMessage" class="auth-error">{{ errorMessage }}</div>
            <button type="submit" class="auth-button" :disabled="busy">{{ busy ? 'signing in…' : 'sign in' }}</button>
          </form>
          <div class="auth-footer">
            <router-link to="/register" class="auth-link">create an account</router-link>
          </div>
        </template>
        <template v-else>
          <h2 class="auth-title">authenticator code</h2>
          <p class="auth-subtitle">Enter the 6-digit code from your authenticator app for {{ email }}, or a recovery code.</p>
          <form @submit.prevent="submitCode" class="auth-form">
            <div class="auth-field">
              <label class="auth-label" for="login-code">code</label>
              <input id="login-code" v-model="code" type="text" autocomplete="one-time-code" spellcheck="false" placeholder="6-digit code or recovery code" required class="auth-input" />
            </div>
            <div v-if="errorMessage" class="auth-error">{{ errorMessage }}</div>
            <button type="submit" class="auth-button" :disabled="busy">{{ busy ? 'verifying…' : 'verify' }}</button>
            <button type="button" class="auth-text-button" :disabled="busy" @click="backToPassword">back</button>
          </form>
        </template>
      </div>
    </div>
  </div>
</template>

<script>

  import _ from 'lodash'

  export default {
    data () {

      return {
        email: ``,
        password: ``,
        code: ``,
        challenge: ``,
        step: `password`,
        errorMessage: ``,
        busy: false
      }
    },
    methods: {
      async login (event) {

        if (!String(this.email).trim().length || !String(this.password).trim().length) {

          return event.preventDefault()
        }

        const body = {
          email: this.email,
          password: this.password
        }

        this.errorMessage = ``
        this.busy = true

        try {

          const url = `${VUE_APP_MANAGER_SERVER_URL}/account/login`

          const response = await this.$http.post(url, body, {
            headers: {
              'Content-Type': 'application/json',
            }
          })

          if (response.data && response.data.twoFactorRequired) {

            this.challenge = response.data.challenge
            this.code = ``
            this.step = `code`
            this.busy = false

            return
          }

          window.location.assign(response.data.redirectUrl)
        } catch (error) {

          console.log(`error logging in:`)
          console.log(error)

          if (error.response && error.response.status === 401) {

            const data = error.response.data

            this.errorMessage = this.step === `code` && typeof data === `string` && data
              ? data
              : `incorrect email or password`
          } else if (error.response && error.response.status === 429) {

            const data = error.response.data

            this.errorMessage = typeof data === `string` && data
              ? data
              : (data && data.error) || `too many sign-in attempts. try again later`
          } else if (error.response && error.response.status === 403 && typeof error.response.data === 'string' && error.response.data) {

            this.errorMessage = error.response.data
          } else {

            this.errorMessage = `something went wrong signing in — please try again`
          }

          this.busy = false

          return event.preventDefault()
        }
      },
      async submitCode (event) {

        if (!String(this.code).trim().length || !this.challenge) {

          return event.preventDefault()
        }

        this.errorMessage = ``
        this.busy = true

        try {

          const response = await this.$http.post(`${VUE_APP_MANAGER_SERVER_URL}/account/login/totp`, {
            challenge: this.challenge,
            code: this.code
          })

          window.location.assign(response.data.redirectUrl)
        } catch (error) {

          const status = error.response && error.response.status
          const data = error.response && error.response.data

          if (status === 401) {

            this.errorMessage = typeof data === `string` && data ? data : `that code is not valid`
          } else if (status === 429) {

            this.errorMessage = typeof data === `string` && data ? data : `too many sign-in attempts. try again later`
          } else if (status === 403 && typeof data === `string` && data) {

            this.errorMessage = data
          } else {

            this.errorMessage = `something went wrong signing in — please try again`
          }

          this.busy = false
        }
      },
      backToPassword () {

        this.step = `password`
        this.challenge = ``
        this.code = ``
        this.errorMessage = ``
        this.busy = false
      }
    },
    components: {
    }
  }
</script>

<style scoped>

.auth-page {
  min-height: 100vh;
  background-color: #181916;
  font-family: 'Helvetica Neue', 'Avenir', Helvetica, Arial, sans-serif;
}

.auth-topbar {
  display: flex;
  align-items: center;
  height: 40px;
  background-color: #0f0f0d;
  border-bottom: 1px solid #2a2a27;
  padding: 0 12px;
}

.auth-brand {
  flex-shrink: 0;
}

.auth-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - 40px);
  padding: 40px 20px;
}

.auth-card {
  width: 100%;
  max-width: 400px;
  background-color: #1e1e1b;
  border: 1px solid #2a2a27;
  border-radius: 8px;
  padding: 40px 36px;
}

.auth-title {
  font-size: 1.4rem;
  font-weight: 600;
  color: #e8e8e6;
  margin: 0 0 6px 0;
}

.auth-subtitle {
  font-size: 0.82rem;
  color: #707070;
  margin: 0 0 32px 0;
}

.auth-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.auth-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.auth-label {
  font-size: 0.75rem;
  font-weight: 500;
  color: #9ad12b;
  text-transform: lowercase;
  letter-spacing: 0.03em;
}

.auth-input {
  background-color: #141412;
  border: 1px solid #2a2a27;
  border-radius: 5px;
  padding: 10px 12px;
  font-size: 0.85rem;
  color: #e8e8e6;
  outline: none;
  transition: border-color 0.15s;
  font-family: inherit;
}

.auth-input::placeholder {
  color: #4a4a47;
}

.auth-input:focus {
  border-color: #9ad12b;
}

.auth-button {
  margin-top: 8px;
  padding: 10px 0;
  background-color: #9ad12b;
  color: #0f0f0d;
  border: none;
  border-radius: 5px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;
  font-family: inherit;
}

.auth-button:hover:not(:disabled) {
  background-color: #b8f046;
}

.auth-button:disabled {
  opacity: 0.6;
  cursor: default;
}

.auth-text-button {
  padding: 0;
  background: none;
  border: none;
  color: #707070;
  font-size: 0.78rem;
  cursor: pointer;
  font-family: inherit;
}

.auth-text-button:hover:not(:disabled) {
  color: #9ad12b;
}

.auth-error {
  color: #e05252;
  font-size: 0.78rem;
}

.auth-footer {
  margin-top: 24px;
  text-align: center;
}

.auth-link {
  color: #707070;
  font-size: 0.78rem;
  text-decoration: none;
  transition: color 0.15s;
}

.auth-link:hover {
  color: #9ad12b;
}

</style>
